import { useCampersList } from '../hooks/useCamper';
import { Link } from 'react-router-dom';

// Icons component for highlights
const HighlightIcon = ({ iconName }) => {
  const icon = iconName && iconName.trim() ? iconName.trim() : 'info';
  return (
    <span
      className="material-symbols-outlined text-gray-600 text-2xl"
      style={{ fontVariationSettings: '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48' }}
    >
      {icon}
    </span>
  );
};

function CampersSection() {
  const { campers, isLoading } = useCampersList();

  if (isLoading) return <div>Loading...</div>;

  return (
    <section className="py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12 px-4">
          Las casas sobre ruedas para tu próxima aventura
        </h2>
        
        <div className="md:px-8">
          <div className="relative md:static">
            {/* Left gradient overlay */}
            <div className="absolute left-0 top-0 bottom-4 w-[10vw] bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none md:hidden" />
            
            {/* Right gradient overlay */}
            <div className="absolute right-0 top-0 bottom-4 w-[10vw] bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none md:hidden" />
            
            <div className="flex overflow-x-auto gap-4 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-8 pb-4 md:pb-0 snap-x snap-mandatory px-[10vw] md:px-0 -mx-[10vw] md:mx-0 scrollbar-hide">
              {campers.map((camper, index) => {
                const imageUrl = camper.mainImage && camper.mainImage.fields ? camper.mainImage.fields.file.url : camper.mainImage;
                return (
                  <div 
                    key={camper.slug} 
                    className={`
                      bg-white rounded-3xl flex flex-col hover:shadow-lg transition-all duration-300 
                      flex-shrink-0 w-[80%] md:w-auto snap-center min-h-[600px]
                      ${index === 0 ? 'ml-4 md:ml-0' : ''} 
                      ${index === campers.length - 1 ? 'mr-4 md:mr-0' : ''}
                    `}
                  >
                    {/* Image section - full width */}
                    <div className="relative overflow-hidden rounded-t-3xl aspect-square group">
                      <img
                        src={imageUrl}
                        alt={`Furgoneta camper ${camper.name} en Galicia - Interior y exterior de la autocaravana`}
                        className="w-full h-64 object-cover rounded-t-2xl"
                        loading="lazy"
                      />
                    </div>
                    
                    {/* Content section with padding */}
                    <div className="flex-grow flex flex-col p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-6">
                        {camper.name}
                      </h3>

                      <div className="space-y-4 flex-grow">
                        {/* Regular features first */}
                        {camper.highlights
                          .filter(highlight => !highlight.optional)
                          .map((highlight, index) => (
                            <div key={index} className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center">
                                <HighlightIcon iconName={highlight.icon} />
                              </div>
                              <span className="text-gray-600">
                                {highlight.text}
                              </span>
                            </div>
                          ))}

                        {/* Optional features */}
                        {camper.highlights
                          .filter(highlight => highlight.optional)
                          .map((highlight, index) => (
                            <div 
                              key={`optional-${index}`} 
                              className="flex items-center gap-3 border border-dashed border-gray-300 rounded-lg p-2 bg-gray-50"
                            >
                              <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400">
                                <HighlightIcon iconName={highlight.icon} />
                              </div>
                              <span className="text-gray-400 text-sm">
                                {highlight.text}
                                <span className="ml-1">(extra)</span>
                              </span>
                            </div>
                          ))}
                      </div>

                      {/* Button section */}
                      <Link
                        to={`/campers/${camper.slug}`}
                        className="block w-full text-center bg-[#95B1A4] hover:bg-[#95B1A4]/90 text-white font-medium py-3 px-6 rounded-xl transition-colors duration-300 mt-8"
                      >
                        Ver detalles
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Scroll indicators */}
          <div className="flex justify-center gap-2 mt-6 md:hidden">
            {campers.map((_, index) => (
              <div 
                key={index} 
                className="w-2 h-2 rounded-full bg-gray-300"
                aria-hidden="true"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default CampersSection;
